import {ColumnOrGroupSpec, ColumnSpec, GridConfig, GridModel} from '@xh/hoist/cmp/grid';
import {HoistModel, managed} from '@xh/hoist/core';
import {Store, StoreRecord} from '@xh/hoist/data';
import {PanelModel} from '@xh/hoist/desktop/cmp/panel';
import {SpecAssistantModel} from '../spec/assistant/SpecAssistantModel';
import {GridSpecConfiguratorModel} from './configurator/GridSpecConfiguratorModel';
import {GridSpecBinder} from './GridSpecBinder';
import {buildColumnSpecs, buildRowClassRules, buildSortBy, SUMMARY_ROW_CLASS} from './GridSpecBuilder';
import {GridSpecModel, GridSpecModelConfig} from './GridSpecModel';
import {GridSpecOp} from './GridSpecOps';
import {GRID_SPEC_VERSION, GridSpecAgOptions} from './GridSpecTypes';
import gridSpecOpsSchema from './gridSpecOps.schema.json';

export interface ConfigurableGridConfig extends GridSpecModelConfig {
    /** Title of the grid - shown in the configurator and given to the AI assistant as context. */
    title?: string;
    /** Additional GridModel config (store, treeMode etc.). Columns, sort and row styles come from the spec. */
    gridConfig?: Omit<GridConfig, 'columns' | 'sortBy' | 'groupBy'>;
    /** Host-provided base ColumnSpec per field - see BuildColumnsOptions. */
    baseColumn?: (field: string) => ColumnSpec;
    treeColumn?: string;
    /** Host hook to wrap the built columns - see BuildColumnsOptions. */
    wrapColumns?: (columns: ColumnOrGroupSpec[]) => ColumnOrGroupSpec[];
    /** Records to total in the summary row - defaults to the (filtered) store records. */
    summaryRecords?: (store: Store) => StoreRecord[];
}

export interface LinkedGridConfig {
    gridConfig?: Omit<GridConfig, 'columns' | 'sortBy' | 'groupBy'>;
    wrapColumns?: (columns: ColumnOrGroupSpec[]) => ColumnOrGroupSpec[];
}

/**
 * A grid configured by a GridSpec, with the configurator panel and AI assistant that edit it.
 * Hosts create one of these, feed data to `gridModel.store` (or connect a Cube View to it), and
 * render it with `configurableGrid()`.
 *
 * A host showing several grids with the same columns adds the others with `createLinkedGrid()`. Each one gets its own
 * binder to the same spec, so a change made in the configurator reaches all of them. Only the first grid reports its
 * own layout changes (resize, reorder) back to the spec.
 */
export class ConfigurableGridModel extends HoistModel {
    @managed readonly specModel: GridSpecModel;
    @managed readonly gridModel: GridModel;
    @managed readonly binder: GridSpecBinder;
    @managed private readonly linkedGrids: GridModel[] = [];
    @managed private readonly linkedBinders: GridSpecBinder[] = [];
    @managed readonly configuratorModel: GridSpecConfiguratorModel;
    @managed readonly assistantModel: SpecAssistantModel;
    @managed readonly panelModel = new PanelModel({
        modalSupport: {canOutsideClickClose: false},
        showModalToggleButton: false,
        collapsible: false,
        resizable: false
    });

    readonly title: string;
    private readonly baseColumn: (field: string) => ColumnSpec;
    private readonly treeColumn: string;

    constructor(config: ConfigurableGridConfig) {
        super();

        const {title, gridConfig = {}, baseColumn, treeColumn, wrapColumns, summaryRecords, ...specConfig} = config;
        this.title = title;
        this.baseColumn = baseColumn;
        this.treeColumn = treeColumn;

        const specModel = (this.specModel = new GridSpecModel(specConfig));

        this.gridModel = this.buildGridModel(gridConfig, wrapColumns);
        this.binder = new GridSpecBinder({specModel, gridModel: this.gridModel, baseColumn, treeColumn, wrapColumns, summaryRecords});
        this.assistantModel = new SpecAssistantModel({
            kind: 'grid',
            specModel,
            schemaVersion: GRID_SPEC_VERSION,
            outputSchema: gridSpecOpsSchema,
            rows: () => this.gridModel.store.allRecords.map(r => r.data),
            title,
            hint: 'Describe the change, e.g. "group by sector, sort by market value desc, totals at the bottom".'
        });
        this.configuratorModel = new GridSpecConfiguratorModel(specModel, this.assistantModel, () => this.autosizeColumns());
    }

    /** Another grid showing the same spec - see the class doc. The host owns its store and renders it. */
    createLinkedGrid({gridConfig = {}, wrapColumns}: LinkedGridConfig = {}): GridModel {
        const {specModel, baseColumn, treeColumn} = this,
            gridModel = this.buildGridModel(gridConfig, wrapColumns);

        this.linkedGrids.push(gridModel);
        this.linkedBinders.push(new GridSpecBinder({specModel, gridModel, baseColumn, treeColumn, wrapColumns, specToGridOnly: true}));
        return gridModel;
    }

    get isConfiguring(): boolean {
        return this.panelModel.isModal;
    }

    get agOptions(): GridSpecAgOptions {
        return this.specModel.spec.agOptions;
    }

    /**
     * Fit the columns to their content now. In managed mode fixed widths would win over autosizing,
     * so they are cleared from the spec first (one undoable step); otherwise the autosized widths
     * flow back into the spec as a grid column layout change.
     */
    autosizeColumns() {
        const {specModel, gridModel} = this;
        if (gridModel.autosizeOptions.mode === 'managed') {
            const ops: GridSpecOp[] = specModel.leafColumns.filter(c => c.width).map(c => ({op: 'updateColumn', field: c.field, changes: {width: null}}));
            if (ops.length) specModel.dispatch(ops, {source: 'manual', label: 'Autosize columns'});
        }
        [gridModel, ...this.linkedGrids].forEach(it => it.autosizeAsync());
    }

    openConfigurator() {
        this.panelModel.setIsModal(true);
    }

    closeConfigurator() {
        this.panelModel.setIsModal(false);
    }

    private buildGridModel(gridConfig: Omit<GridConfig, 'columns' | 'sortBy' | 'groupBy'>, wrapColumns: LinkedGridConfig['wrapColumns']): GridModel {
        const {specModel, baseColumn, treeColumn} = this,
            {spec, capabilities} = specModel;

        return new GridModel({
            colChooserModel: true,
            ...gridConfig,
            columns: buildColumnSpecs(spec, {baseColumn, treeColumn, wrapColumns}),
            sortBy: capabilities.sorting === false ? [] : buildSortBy(spec),
            rowClassRules: {...gridConfig.rowClassRules, ...buildRowClassRules(() => specModel.spec.rowClassRules)},
            rowClassFn: record => (record.isSummary ? SUMMARY_ROW_CLASS : (gridConfig.rowClassFn?.(record) ?? null))
        });
    }
}
